import React, { FC, FunctionComponent, Suspense, useEffect, useState } from 'react';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { graphql } from 'relay-runtime';
import { CatalogListLoader } from 'src/components/widgets/catalogList';
import { CustomErrorBoundary } from 'src/components/widgets/customErrorBoundary';
import { ErrorMessage } from 'src/shared/globals';
import { backofficeUrl, T, toGlobalID, toLocalID } from 'src/shared/helpers';
import { Mutable, RelayFragment } from 'src/shared/types';
import styled from 'styled-components';
import { productTemplate_listItem$data } from '../state/queries/__generated__/productTemplate_listItem.graphql';

import { FormControl } from '../../../widgets/formControl';
import { QuestionDialog, useDialogManager } from '../../../widgets/dialog';
import { removeNotification, showNotification } from '../../../widgets/notificationToaster';
import { SearchInput } from '../../../widgets/input';
import { useNavigate } from 'react-router-dom';
import Loader from 'src/components/widgets/loader';
import { useRelayStore } from 'src/relay';
import { DialogOverlay } from '../../dashboard/intro/dialogDel';
import { useProductConfigurationState } from '../state';
import { MerchantProductOption } from '../state/model/modelColorMapping';
import ConnectModelDialog, { PartialSalesChannel } from '../steps/6-done/connectModelDialog';
import { ECommerce, templateCatalogImportMutation } from './__generated__/templateCatalogImportMutation.graphql';
import { templateCatalogQuery, templateCatalogQuery$data } from './__generated__/templateCatalogQuery.graphql';
import SearchTemplateCategory from './searchTemplateCategory';
import TemplateCatalogListQuery from './templateCatalogList';

interface TemplateCatalogProps {
    salesChannelOfflineID: string;
    targetSalesChannelID: string;
    targetEcommerce: ECommerce;
    targetProductSKU: string;
    targetProductName: string;
    onTemplateImported?: (model: templateCatalogQuery$data['model'], salesChannels: templateCatalogQuery$data['salesChannels']) => void;
}

const OverlayImportTemplate = styled(DialogOverlay)`
    z-index: 9999;
    justify-content: center;
`;

const SearchContainer = styled.div<{ hasCategory: boolean }>`
    display: grid;
    grid-gap: 30px;

    ${(props) =>
        props.hasCategory &&
        `
        grid-template-columns: 1fr 426px;
    `}

    ${(props) =>
        !props.hasCategory &&
        `
        grid-template-columns: 1fr;
    `}
`;

const TemplateCatalog: FunctionComponent<TemplateCatalogProps> = ({
    salesChannelOfflineID,
    targetSalesChannelID,
    targetEcommerce,
    targetProductSKU,
    targetProductName,
    onTemplateImported
}) => {
    const [search, setSearch] = useState('');
    const [categoryID, setCategoryID] = useState<number | null>(null);
    const [productTemplateID, setProductTemplateID] = useState('');
    const [selectedProductTemplateName, setSelectedProductTemplateName] = useState('');
    const { showDialog, closeDialog } = useDialogManager();

    const navigate = useNavigate();

    const [importTemplateProduct, isImportTemplateProductInProgress] = useMutation<templateCatalogImportMutation>(graphql`
        mutation templateCatalogImportMutation(
            $salesChannelOfflineID: String!
            $templateProductID: String!
            $targetProductSKU: String!
            $targetProductName: String!
            $salesChannelID: String!
            $ecommerce: ECommerce!
        ) {
            importProductTemplate(
                salesChannelOfflineID: $salesChannelOfflineID
                templateProductID: $templateProductID
                targetProductSKU: $targetProductSKU
                targetProductName: $targetProductName
                salesChannelID: $salesChannelID
                ecommerce: $ecommerce
            )
        }
    `);

    const handleSearch = (search: string) => {
        setSearch(search);
    };

    const handleProductClick = async (productTemplate: Omit<RelayFragment<productTemplate_listItem$data>, ' $fragmentType'>) => {
        showDialog(
            'confirm-importing-template',
            <QuestionDialog
                buttons={[
                    {
                        label: 'No',
                        outline: true,
                        onClick: () => closeDialog('confirm-importing-template')
                    },
                    {
                        label: 'Yes',
                        onClick: () =>
                            handleImportFromTemplate(
                                salesChannelOfflineID,
                                productTemplate.id,
                                productTemplate.name,
                                targetSalesChannelID,
                                targetEcommerce,
                                targetProductSKU,
                                targetProductName
                            )
                    }
                ]}
                message={<span>{T._('Do you want to proceed to import this template?', 'Admin')}</span>}
            />
        );
    };

    const handleImportFromTemplate = async (
        salesChannelOfflineID: string,
        templateProductID: string,
        templateName: string,
        salesChannelID: string,
        ecommerce: ECommerce,
        targetProductSKU: string,
        targetProductName: string
    ) => {
        closeDialog('confirm-importing-template');

        const loadingNotification = showNotification({
            type: 'default',
            message: T._('Importing from template in progress...', 'Admin')
        });

        importTemplateProduct({
            variables: {
                salesChannelOfflineID,
                templateProductID,
                targetProductSKU,
                targetProductName,
                salesChannelID,
                ecommerce
            },
            onCompleted: (response) => {
                removeNotification(loadingNotification);
                setSelectedProductTemplateName(templateName);
                let productID = toGlobalID(response.importProductTemplate, 'product');
                targetEcommerce !== 'NONE' ? setProductTemplateID(productID) : navigate(backofficeUrl(`/products`));
            },
            onError: () => {
                removeNotification(loadingNotification);
                closeDialog('confirm-importing-template');
                showNotification({
                    type: 'danger',
                    message: T._('An error occurred while importing from template.', 'Admin'),
                    dismiss: {
                        duration: 3000
                    }
                });
            }
        });
    };

    return (
        <React.Fragment>
            <CustomErrorBoundary render={(error) => <ErrorMessage>{error?.message}</ErrorMessage>}>
                <FormControl>
                    <SearchContainer hasCategory={true}>
                        <SearchInput fullWidth placeholder={T._('Search by template name ...', 'Admin')} onSearch={handleSearch} />
                        <Suspense fallback={null}>
                            <SearchTemplateCategory
                                value={categoryID}
                                onChange={(id) => (id && id.split('_').length > 1 ? setCategoryID(parseInt(id.split('_')[1])) : setCategoryID(null))}
                            />
                        </Suspense>
                    </SearchContainer>
                </FormControl>

                <Suspense fallback={CatalogListLoader}>
                    <TemplateCatalogListQuery search={search} categoryID={categoryID} onProductClick={handleProductClick} />
                </Suspense>
            </CustomErrorBoundary>
            {isImportTemplateProductInProgress && (
                <OverlayImportTemplate>
                    <Loader />
                </OverlayImportTemplate>
            )}
            {productTemplateID != '' && (
                <Suspense
                    fallback={
                        <OverlayImportTemplate>
                            <Loader />
                        </OverlayImportTemplate>
                    }
                >
                    <MappingModelQuery productID={productTemplateID} templateName={selectedProductTemplateName}></MappingModelQuery>
                </Suspense>
            )}
        </React.Fragment>
    );
};

const MappingModelQuery: FC<{ productID: string; templateName: string }> = ({ productID, templateName }) => {
    const invalidate = useRelayStore((state) => state.invalidateRelayStore);
    const { showDialog, closeDialog } = useDialogManager();
    const { state, dispatch } = useProductConfigurationState();
    const { model, salesChannels } = useLazyLoadQuery<templateCatalogQuery>(
        graphql`
            query templateCatalogQuery($productId: ID!) {
                salesChannels(status: ACTIVE, ignoreNoneECommerce: true) {
                    id
                    ecommerceSettings {
                        ecommerceID
                        ecommerceName
                        hasProductsApi
                        isMarketplace
                        remoteUrl
                        definitiveUrl
                    }
                }

                model(id: $productId) {
                    id
                    statusID
                    name
                    code
                    salesChannelID
                    salesChannel {
                        ecommerceSettings {
                            ecommerceName
                        }
                    }

                    additionalCodes {
                        code
                        salesChannelID
                        merchantProductName
                        salesChannel {
                            ecommerceSettings {
                                ecommerceName
                                remoteUrl
                            }
                        }
                    }

                    colors {
                        colorID
                        name
                        code
                        colorProviderCode
                        position
                    }

                    merchantProductConnectedOptions {
                        salesChannelID
                        sku
                        optionID
                        label
                        isGlobal
                        values {
                            valueID
                            label
                        }
                    }
                }
            }
        `,
        { productId: productID }
    );

    const navigate = useNavigate();

    useEffect(() => {
        if ((model?.colors ?? []).length == 0) navigate(backofficeUrl(`/products`));
        else
            showDialog(
                'mapping-variants',
                <ConnectModelDialog
                    forAddingNewSalesChannel={false}
                    forImportingTemplates={true}
                    buttonSaveText={T._('Save', 'Admin')}
                    productID={model?.id ?? ''}
                    productName={templateName}
                    additionalCodes={(model?.additionalCodes ?? []).map((c) => {
                        return { code: c.code, salesChannelID: c.salesChannelID, merchantProductName: c.merchantProductName };
                    })}
                    productColors={(model?.colors ?? []).map((c) => {
                        return { colorID: c.colorID, code: c.code, name: c.name, guid: '', colorCode: c.code };
                    })}
                    salesChannels={salesChannels.filter((sc) => toLocalID(sc.id) !== toLocalID(model?.salesChannelID ?? '')) as Mutable<PartialSalesChannel>[]}
                    selectedSalesChannelID={model?.salesChannelID ?? ''}
                    productSalesChannelID={model?.salesChannelID ?? ''}
                    modelColorMappings={[]}
                    merchantProductOptionsConnected={(model?.merchantProductConnectedOptions ?? []) as MerchantProductOption[]}
                    onConfirm={(product, salesChannelID, productColorMappingsToSave) => {
                        closeDialog('mapping-variants');
                        closeDialog('ChooseTemplate');
                        // Navigate commented out and used redirect to prevent product page not being updated
                        // navigate(backofficeUrl(`/products`));
                        window.location.href = backofficeUrl(`/products`);
                    }}
                    onCloseDialog={() => {
                        closeDialog('mapping-variants');
                        closeDialog('ChooseTemplate');
                        // Navigate commented out and used redirect to prevent product page not being updated
                        // navigate(backofficeUrl(`/products`));
                        window.location.href = backofficeUrl(`/products`);
                    }}
                />
            );
    }, []);

    return null;
};

export default TemplateCatalog;
